import { invert } from '@transcend-io/type-utils';

import { IntlMessageType } from '@main/internationalization';

import * as endpoints from './endpoints';
import { PrivacyCenterCustomizableMessageName } from './enums';
import { PageType } from './schema/enums';
import { PrivacyCenterModule } from './types';
import {
  mkContextMap,
  PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER,
} from './worker-constants';

/**
 * Lookup from page type to pathname
 */
export const PAGE_PATHNAMES: { [k in PageType]: string } = {
  [PageType.ManageYourPrivacy]: '/my-privacy-choices',
  [PageType.HomePage]: '/',
  [PageType.Policies]: '/policies',
  [PageType.TrackingTechnologies]: '/trackers',
  [PageType.RequestsProcessedReporting]: '/requests-processed-reporting',
  [PageType.CommunicationsPreferences]: '/communications-preferences',
  [PageType.SaleOfInfo]: '/sale-of-info',
};

/**
 * Lookup from pathname on privacy center to page type
 */
export const PATHNAME_TO_PAGE_TYPE = invert(PAGE_PATHNAMES);

/** Name of file when exporting internationalized messages */
export const EXPORT_MESSAGES_PC_FILE_NAME = 'privacy-center-translations.csv';

/**
 * Override the default validation type for loose messages
 */
export const LOOSE_MESSAGE_SUPPORTS_HTML: {
  [k in PrivacyCenterCustomizableMessageName]?: boolean;
} = {
  [PrivacyCenterCustomizableMessageName.AboutTranscendDescription]: true,
  [PrivacyCenterCustomizableMessageName.TakeControlDescription]: true,
  [PrivacyCenterCustomizableMessageName.TrackingTechnologiesInfo]: true,
};
export const MESSAGE_TYPE_SUPPORTS_HTML: IntlMessageType[] = [
  IntlMessageType.TemplateTemplate,
];

export const PRIVACY_CENTER_MODULE_CONTEXT_MAP = mkContextMap({
  [PrivacyCenterModule.PrivacyCenter]: {
    ...PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER[
      PrivacyCenterModule.PrivacyCenter
    ],
    endpoint: endpoints.privacyCenter,
  },
  [PrivacyCenterModule.Policies]: {
    ...PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER[PrivacyCenterModule.Policies],
    endpoint: endpoints.privacyCenterPolicies,
  },
  [PrivacyCenterModule.Messages]: {
    ...PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER[PrivacyCenterModule.Messages],
    endpoint: endpoints.privacyCenterTranslatedMessages,
  },
  [PrivacyCenterModule.Subjects]: {
    ...PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER[PrivacyCenterModule.Subjects],
    endpoint: endpoints.privacyCenterSubjects,
  },
  [PrivacyCenterModule.RequestsProcessedStats]: {
    ...PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER[
      PrivacyCenterModule.RequestsProcessedStats
    ],
    endpoint: endpoints.requestsProcessedDisclosureStats,
  },
  [PrivacyCenterModule.Purposes]: {
    ...PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER[PrivacyCenterModule.Purposes],
    endpoint: endpoints.privacyCenterPurposes,
  },
  [PrivacyCenterModule.WorkflowConfigs]: {
    ...PRIVACY_CENTER_MODULE_CONTEXT_MAP_WORKER[
      PrivacyCenterModule.WorkflowConfigs
    ],
    endpoint: endpoints.privacyCenterWorkflows,
  },
});

/**
 * Map of privacy center module contexts
 */
export type PrivacyCenterModuleContextMap =
  typeof PRIVACY_CENTER_MODULE_CONTEXT_MAP;

/**
 * Pages that do not require the take control modal
 */
export const PAGES_WITHOUT_TAKE_CONTROL: string[] = [
  PAGE_PATHNAMES[PageType.SaleOfInfo],
  PAGE_PATHNAMES[PageType.CommunicationsPreferences],
  PAGE_PATHNAMES[PageType.ManageYourPrivacy],
].map((page) => page.replace('/', ''));

/**
 * The set of mutations that do not require sombra authentication
 */
export const PRIVACY_CENTER_MUTATIONS_WITHOUT_AUTH = [
  'privacyCenterEmailLogin',
  'verifyPrivacyCenterLoginEmail',
  'wrongLoginEmail',
  'wrongRequestToken',
  'unauthenticatedPreferenceUpdate',
] as const;

/**
 * The set of mutations that require sombra authentication
 */
export const PRIVACY_CENTER_MUTATIONS_WITH_AUTH = [
  'makeDataSubjectRequest',
  'makeDataSubjectRequests',
  'revokeRequest',
  'sombraDataSubjectLogin',
  'dataSubjectDownloadCEK',
  'verifyRequestToken',
  'generateParentPrivacyCenterLoginToken',
  'dataSubjectUpdateConsentPreferences',
] as const;

/**
 * The set of queries that do not require sombra authentication
 */
export const PRIVACY_CENTER_QUERIES_WITHOUT_AUTH = [
  'privacyCenter',
  'privacyCenterPolicies',
  'privacyCenterPurposes',
  'privacyCenterSubjects',
  'privacyCenterTrackingTechnology',
  'privacyCenterTranslatedMessages',
  'privacyCenterWorkflows',
  'requestsProcessedDisclosureStats',
] as const;

/**
 * The set of queries that require sombra authentication
 */
export const PRIVACY_CENTER_QUERIES_WITH_AUTH = [
  'dataCollectionsRequestFileCount',
  'dataSubjectConsentPreferences',
  'downloadFiles',
  'downloadFilesCountsOnly',
  'requestHistory',
] as const;

/**
 * The set of privacy center mutations supported by the backend
 */
export const PRIVACY_CENTER_MUTATIONS = [
  ...PRIVACY_CENTER_MUTATIONS_WITHOUT_AUTH,
  ...PRIVACY_CENTER_MUTATIONS_WITH_AUTH,
];

/**
 * The set of privacy center queries supported by the backend
 */
export const PRIVACY_CENTER_QUERIES = [
  ...PRIVACY_CENTER_QUERIES_WITHOUT_AUTH,
  ...PRIVACY_CENTER_QUERIES_WITH_AUTH,
];
